import {ArrowDownIcon, ArrowUpIcon} from '../../../partials/icons';
import {Menu, MenuItem, MenuText} from '../../../menu/menu';
import {pickLanguage} from '../../../../../helpers/auth-helper';
import type {Language} from '../../../../../helpers/i18n';
import {languageFlags, languageNames, toFlag, toLanguage} from '../../../../../helpers/i18n';
import {useSessionUser} from '../../../../../hooks/use-session';
import {useLanguage, usePickedLanguage, useTranslation} from '../../../../../hooks/use-translation';

/** A flag of `flag-icons`, before the name of a language. */
const Flag = ({country}: {country: string}): React.JSX.Element => (
    <span className={`fi fi-${country}`} aria-hidden="true" />
);

/**
 * The language menu (F7): the language of the user's account, or one picked on this terminal. Every language is named
 * in its own words and has its flag; the one of the account the flag of the account's country (`nl_BE` → Belgium).
 */
export const TopNavLanguage = (): React.JSX.Element | null => {
    const t = useTranslation();
    const user = useSessionUser();
    const language = useLanguage();
    const pickedLanguage = usePickedLanguage();

    if (null === user) {
        return null;
    }

    const accountLanguage = toLanguage(user.locale);
    const accountFlag = toFlag(user.locale);

    return (
        <Menu
            shortcut="F7"
            aria-label={t('topNav.languageButton', {language: languageNames[language]})}
            label={
                <>
                    <Flag country={null === pickedLanguage ? accountFlag : languageFlags[pickedLanguage]} />
                    <span lang={language}>{languageNames[language]}</span>
                </>
            }
        >
            <MenuText>{t('topNav.language')}</MenuText>
            <MenuItem checked={null === pickedLanguage} onSelect={() => pickLanguage(null)}>
                <Flag country={accountFlag} />
                {t('topNav.accountLanguage', {language: languageNames[accountLanguage]})}
            </MenuItem>
            {(Object.keys(languageNames) as Language[]).map(option => (
                <MenuItem
                    key={option}
                    lang={option}
                    checked={option === pickedLanguage}
                    onSelect={() => pickLanguage(option)}
                >
                    <Flag country={languageFlags[option]} />
                    {languageNames[option]}
                </MenuItem>
            ))}
            <MenuText note>
                <kbd aria-label={t('topNav.menuArrowUp')}>
                    <ArrowUpIcon />
                </kbd>
                <kbd aria-label={t('topNav.menuArrowDown')}>
                    <ArrowDownIcon />
                </kbd>
                {t('topNav.menuPick')}, <kbd>Enter</kbd>
                {t('topNav.menuConfirm')}, <kbd>F7</kbd>
                <kbd>Esc</kbd>
                {t('topNav.menuClose')}
            </MenuText>
        </Menu>
    );
};
